import React, { useEffect, useMemo } from 'react';
import {
  Button,
  Card,
  Checkbox,
  Collapse,
  Divider,
  Flex,
  Form,
  Select,
  Skeleton,
  Typography,
  message,
} from 'antd';
import { useTranslation } from 'react-i18next';

import InternshipOfferCard from './InternshipOfferCard';
import type { ApplyableStatus } from './InternshipApplicationComponent';
import {
  GetActiveInternshipOffers,
  GetOrganizations,
  GetStudentApplications,
  GetStudyLines,
} from '../../helper/apiHelper';
import type {
  InternshipApplication,
  ReducedInternship,
  ReducedOrganization,
  StudyLine,
} from '../../Types/interfaces';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { QUERY_KEYS } from '../../Constants/queryKeys';
import { EMPTY_ARRAY } from '../../Constants/constants';
import { useStudent } from '../../hooks/useStudent';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { useUserOrganizationsIds } from '../../hooks/useOrganizations';
import { NoData } from '../NoData';
import noInternshipOffersImg from '../../Assets/Images/noInternshipOffers.png';

const filterByCompany = (
  internships: ReducedInternship[],
  id: number
): ReducedInternship[] => {
  return internships.filter((value) => {
    return value.organizationId === id;
  });
};

const filterByNumberOfHours = (
  internships: ReducedInternship[],
  interval: [number, number]
): ReducedInternship[] => {
  return internships.filter((value) => {
    return (
      value.numberOfHours >= interval[0] && value.numberOfHours <= interval[1]
    );
  });
};

const filterPreferredStudyLines = (
  internships: ReducedInternship[],
  preferredStudyLineIds?: number[]
): ReducedInternship[] => {
  return internships.filter((internship) =>
    internship.internshipSessions.some((session) =>
      preferredStudyLineIds?.includes(session.studyLineId)
    )
  );
};

type OrganizedInternships = {
  [studyLineId: number]: {
    [sessionId: number]: ReducedInternship[];
  };
};

const InternshipListComponent = (): React.ReactElement => {
  const loggedInUser = useCurrentUser();
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const [messageApi, contextHolder] = message.useMessage();
  const queryClient = useQueryClient();
  const watchedValues = Form.useWatch([], form);
  const { data: student } = useStudent(loggedInUser.id);
  const { data: studentOrganizationsIds } = useUserOrganizationsIds(
    loggedInUser.id
  );

  const results = useQueries({
    queries: [
      { queryKey: QUERY_KEYS.ORGANIZATIONS, queryFn: GetOrganizations },
      {
        queryKey: [QUERY_KEYS.ACTIVE_INTERNSHIP_OFFERS, loggedInUser.id],
        queryFn: async () => {
          const rawInternships = await GetActiveInternshipOffers();
          try {
            const studentApplications: InternshipApplication[] =
              await GetStudentApplications(loggedInUser.id);

            return rawInternships.filter(
              (internship) =>
                !studentApplications.some(
                  (application) =>
                    application.internshipOfferId === internship.id
                )
            );
          } catch {
            return rawInternships;
          }
        },
        enabled: !!loggedInUser.id,
      },
      { queryKey: QUERY_KEYS.STUDY_LINES, queryFn: GetStudyLines },
    ],
  });

  const organizations: ReducedOrganization[] = results[0].data ?? EMPTY_ARRAY;
  const internships: ReducedInternship[] = results[1].data ?? EMPTY_ARRAY;
  const studyLines: StudyLine[] = results[2].data ?? EMPTY_ARRAY;
  const isLoading = results.some((result) => result.isLoading);
  const hasError = results.some((result) => result.isError);

  useEffect(() => {
    if (hasError) {
      messageApi.error({
        content: t('internshipListComponent.fetchError'),
      });
    }
  }, [hasError, messageApi, t]);

  useEffect(() => {
    if (organizations.length === 1) {
      form.setFieldValue('organizationId', organizations[0].id);
    }
  }, [form, organizations]);

  const filteredInternships = useMemo(() => {
    if (!internships || internships.length === 0) return [];

    const values = watchedValues || form.getFieldsValue();
    let filterInternships = internships;

    if (values.organizationId !== undefined) {
      filterInternships = filterByCompany(
        filterInternships,
        values.organizationId
      );
    }

    if (values.numberOfHours !== undefined) {
      filterInternships = filterByNumberOfHours(
        filterInternships,
        values.numberOfHours
      );
    }

    if (values.preferredStudyLines === true) {
      filterInternships = filterPreferredStudyLines(
        filterInternships,
        student?.preferredStudyLineIds
      );
    }

    return filterInternships;
  }, [form, internships, watchedValues, student?.preferredStudyLineIds]);

  const { preferredInternships, otherInternships } = useMemo(() => {
    const preferred: OrganizedInternships = {};
    const other: OrganizedInternships = {};

    filteredInternships.forEach((internship) => {
      internship.internshipSessions.forEach((session) => {
        const studyLineId = session.studyLineId;

        // setup preferred and other internships
        if (student?.preferredStudyLineIds.includes(studyLineId)) {
          if (!preferred[studyLineId]) {
            preferred[studyLineId] = {};
          }

          if (!preferred[studyLineId][session.id]) {
            preferred[studyLineId][session.id] = [];
          }

          preferred[studyLineId][session.id].push(internship);
        } else {
          if (!other[studyLineId]) {
            other[studyLineId] = {};
          }

          if (!other[studyLineId][session.id]) {
            other[studyLineId][session.id] = [];
          }

          other[studyLineId][session.id].push(internship);
        }
      });
    });

    return { preferredInternships: preferred, otherInternships: other };
  }, [filteredInternships, student?.preferredStudyLineIds]);

  const getStudyLineName = (studyLineId: number): string => {
    return studyLines.find((s) => s.id === studyLineId)?.name ?? '';
  };

  const getSessionName = (sessionId: number): string => {
    return (
      studyLines
        .flatMap((sl) => sl.internshipSessions)
        .find((s) => s.id === sessionId)?.description ?? ''
    );
  };

  const onReset = (): void => {
    form.resetFields();
  };

  function getOrganizationName(organizationId: number): string | null {
    const organization = organizations.find((o) => o.id === organizationId);
    return organization ? organization.name : null;
  }

  const removeInternship = (id: number): void => {
    queryClient.setQueryData(
      [QUERY_KEYS.ACTIVE_INTERNSHIP_OFFERS, loggedInUser.id],
      (oldData: ReducedInternship[]) => {
        // check if cache is empty
        if (!oldData) return [];
        return oldData.filter((item) => item.id !== id);
      }
    );
  };

  const SkeletonCards: React.ReactElement = (
    <Flex wrap="wrap" gap="middle" style={{ marginTop: 16 }}>
      {Array(6)
        .fill(null)
        .map((_, i) => (
          <Card
            key={i}
            style={{
              width: 300,
              height: 450,
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
            }}
          >
            <Skeleton active paragraph={{ rows: 1 }} />
            <Skeleton.Image style={{ height: 200, width: 250 }} active={true} />
          </Card>
        ))}
    </Flex>
  );

  const renderSessions = (
    sessions: { [sessionId: number]: ReducedInternship[] },
    studyLineId: string,
    studyLineName: string,
    getApplyable: (item: ReducedInternship) => ApplyableStatus
  ): React.ReactNode =>
    Object.entries(sessions).map(([sessionId, items]) => (
      <div key={sessionId}>
        <Typography.Title
          level={5}
          type="secondary"
          style={{ margin: '8px 0 12px' }}
        >
          {t('internshipListComponent.session')}:{' '}
          {getSessionName(parseInt(sessionId))}
        </Typography.Title>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
            gap: 16,
          }}
        >
          {items.map((item) => (
            <InternshipOfferCard
              key={`${studyLineId}-${sessionId}-${item.id}`}
              internship={item}
              organizationName={
                getOrganizationName(item.organizationId) ??
                t('internshipListComponent.na')
              }
              sessionId={parseInt(sessionId)}
              studyLineName={studyLineName}
              applyable={getApplyable(item)}
              removeInternship={removeInternship}
            />
          ))}
        </div>
      </div>
    ));

  const hasResults =
    Object.keys(preferredInternships).length > 0 ||
    Object.keys(otherInternships).length > 0;

  const renderContent = (): React.ReactNode => {
    if (isLoading) return SkeletonCards;

    if (!hasResults) {
      return (
        <NoData
          imgSource={noInternshipOffersImg}
          message={t('internshipListComponent.noAvailableInternshipMessage')}
        />
      );
    }

    return (
      <Flex vertical gap={32} style={{ width: '100%' }}>
        {/* Preferred study lines */}
        {Object.entries(preferredInternships).map(([studyLineId, sessions]) => {
          const studyLineName = getStudyLineName(parseInt(studyLineId));
          return (
            <section key={studyLineId}>
              <Typography.Title level={3} style={{ margin: 0 }}>
                {t('internshipListComponent.studyLine')}: {studyLineName}
              </Typography.Title>
              <Divider style={{ margin: '8px 0 16px' }} />
              <Flex vertical gap={16}>
                {renderSessions(sessions, studyLineId, studyLineName, (item) =>
                  studentOrganizationsIds?.includes(item.organizationId)
                    ? 'HisOwnCompany'
                    : 'Allowed'
                )}
              </Flex>
            </section>
          );
        })}

        {/* Other study lines */}
        <Collapse
          ghost
          items={Object.entries(otherInternships).map(
            ([studyLineId, sessions]) => ({
              key: studyLineId,
              label: (
                <Typography.Text strong style={{ fontSize: 16 }}>
                  {t('internshipListComponent.studyLine')}:{' '}
                  {getStudyLineName(parseInt(studyLineId))}
                </Typography.Text>
              ),
              children: (
                <Flex vertical gap={16}>
                  {renderSessions(
                    sessions,
                    studyLineId,
                    getStudyLineName(parseInt(studyLineId)),
                    () => 'HeIsntOnThatStudyLine'
                  )}
                </Flex>
              ),
            })
          )}
        />
      </Flex>
    );
  };

  return (
    <>
      {contextHolder}
      {/* The filter form stays mounted as long as there are offers, so a
          filter that matches nothing can still be changed or reset. */}
      {internships.length > 0 && (
        <Form
          form={form}
          layout="inline"
          style={{ marginBottom: 24, rowGap: 12 }}
        >
          <Form.Item name="organizationId" style={{ minWidth: 260 }}>
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder={t('internshipListComponent.filter.organization')}
              options={organizations.map((org) => ({
                label: org.name,
                value: org.id,
              }))}
            />
          </Form.Item>
          <Form.Item name="preferredStudyLines" valuePropName="checked">
            <Checkbox>
              {t('internshipListComponent.filter.showPrefOnly')}
            </Checkbox>
          </Form.Item>
          <Form.Item>
            <Button htmlType="button" onClick={onReset}>
              {t('internshipListComponent.filter.reset')}
            </Button>
          </Form.Item>
        </Form>
      )}
      {renderContent()}
    </>
  );
};

export default InternshipListComponent;
